<template>
    <div class="block xl:flex flex-row gap-x-6 xl:w-3/5 xl:pr-4">
        <!-- Column 1 -->
        <div class="xl:w-1/2">
            <div class="mb-4">
                <jet-label for="code" value="Chain Code" />
                <jet-input type="text" class="block w-full" v-model="form.code" ref="code" />
                <jet-input-error :message="$page.props.errors.code" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="name" value="Chain Name" />
                <jet-input type="text" class="block w-full" v-model="form.name" />
                <jet-input-error :message="$page.props.errors.name" class="mt-1" />
            </div>

            <div class="mb-4">
                <toggle id="customer_favourites_visible_only" v-model="form.customer_favourites_visible_only" />
                <jet-label for="customer_favourites_visible_only" value="Customer Favourites Visible Only" class="ml-2 inline-block" />
                <p class="text-sm text-gray-500 mt-1">Customers in this chain only see their favourite products in the catalogue.</p>
                <jet-input-error :message="$page.props.errors.customer_favourites_visible_only" class="mt-1" />
            </div>

            <div class="mb-4">
                <jet-label for="newCustomer" value="New Customer" />
                <div class="flex items-center gap-2">
                    <multiselect class="multi-custom h-8 text-base flex-1"
                        v-model="selectedCustomer"
                        :options="availableCustomers"
                        valueProp="id"
                        trackBy="name"
                        label="name"
                        :searchable="true"
                        :canClear="true"
                        :canDeselect="true"
                        :appendToBody="true"
                    >
                        <template v-slot:option="{ option }">
                            <span>{{ option.name }} </span>
                        </template>
                    </multiselect>
                    <jet-button type="button" class="h-8" @click="addCustomer">
                        <i class="fas fa-plus"></i>
                    </jet-button>
                </div>
            </div>
        </div>

        <!-- Column 2 -->
        <div class="xl:w-1/2">
            <h2 class="mb-2">Customers</h2>
            <div class="text-sm my-1">{{ form.customers.length }} customers attached to this chain</div>

            <div class="space-y-3">
                <div 
                    v-for="(c, index) in form.customers" 
                    :key="'details-' + c.id"
                    class="border rounded p-3 bg-gray-50 relative group"
                >
                    <div class="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <a
                            type="button"
                            @click="removeCustomer(index)"
                            class="text-red-600 hover:text-red-800 cursor-pointer"
                            title="Remove customer"
                        >
                            <i class="fas fa-trash-alt"></i>
                        </a>
                        <a 
                            :href="route('customers.edit', c.id)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-gray-600 hover:text-gray-900"
                            title="Open customer page in new tab"
                        >
                            <i class="fas fa-external-link-alt"></i>
                        </a>
                    </div>

                    <h3 class="font-semibold">{{ c.name }}</h3>

                    <div class="text-sm text-gray-600">
                        {{ customerDetails[c.id].address_1 }}<br>
                        <span v-if="customerDetails[c.id].address_2">
                            {{ customerDetails[c.id].address_2 }}<br>
                        </span>
                        {{ customerDetails[c.id].city }}, 
                        {{ customerDetails[c.id].province }}  
                        {{ customerDetails[c.id].postal_code }}
                    </div>

                    <div class="mt-1 text-sm text-gray-600">
                        <div>{{ customerDetails[c.id].phone }}</div>
                        <div>{{ customerDetails[c.id].email?.split(',').join(', ') }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetButton from '@/Jetstream/Button';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';

import '@vueform/toggle/themes/default.css';

export default {
    props: [
        'form',
        'customers',
    ],

    components: {
        JetLabel,
        JetInput,
        JetInputError,
        JetButton,
        JetSecondaryButton,
        Multiselect,
        Toggle,
    },

    data() {
        return {
            selectedCustomer: null,
            provinceOptions: [
                'Ontario',
                'Manitoba',
                'Saskatchewan',
                'British Columbia',
                'Yukon',
                'Northwest Territories',
                'Nunavut',
                'Quebec',
                'Nova Scotia',
                'New Brunswick',
                'Newfoundland',
                'Prince Edward Island'
            ],
        };
    },

    computed: {
        availableCustomers() {
            const selectedIds = this.form.customers.map(c => c.id)
            return this.customers.filter(c => !selectedIds.includes(c.id))
        },

        customerDetails() {
            const customerMap = {};
            this.customers.forEach(c => {
                customerMap[c.id] = c;
            });
            return customerMap;
        }
    },

    methods: {
        addCustomer() {
        if (this.selectedCustomer) {
            const customer = this.customers.find(c => c.id === this.selectedCustomer);

            if (customer) {
                this.form.customers.push({
                    id: customer.id,
                    name: customer.name,
                });
            }

            this.selectedCustomer = null;
        }
    },
        removeCustomer(index) {
            this.form.customers.splice(index, 1);
        },
    },
};
</script>

<style scoped>
.table th {
    background-color: #f9fafb;
}

.multi-custom {
    min-height: 1.5rem;
}
</style>
